import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import apiClient from '../api/client'
import { errorMessage } from '../api/ApiError'
import type { Diagnostico } from '../types/portal'

/**
 * "¿Pasó algo?" — preguntarle al sistema en lenguaje llano.
 *
 * Cuatro ceros y una tabla vacía no permiten concluir nada: "no pasó nada" y
 * "no lo estoy detectando" se ven igual. Y quien abre esta pantalla casi nunca
 * viene a leer una tabla — viene de un reporte concreto ("un cliente dice que
 * no le llegó el enlace") y necesita preguntar por eso.
 *
 * La respuesta la redacta el modelo, pero SOLO con los datos que el backend le
 * pasa — incidencias, conversaciones vivas, actividad del día y, para Admin,
 * errores de log — y recortados al rol de quien pregunta. Por eso se muestran
 * siempre las fuentes consultadas — para poder comprobar la respuesta en vez
 * de creerle. Y los ids de conversación se filtran contra el contexto real, así
 * que un "Ver traza" nunca lleva a un caso inventado.
 */

// Dos de fallo y dos de actividad, a propósito: el contexto ya no es solo lo
// que se rompió, y si los ejemplos solo preguntan por fallos nadie descubre que
// también contesta "¿quién atendió hoy?".
const EJEMPLOS = [
  '¿Pasó algo en la última hora?',
  '¿Hubo pedidos de atención por asesor hoy?',
  '¿Hay clientes esperando sin respuesta?',
  '¿Qué hizo el equipo hoy?',
]

/**
 * `colapsable` — en la bandeja del asesor arranca cerrado, como una tira
 * delgada que se abre al pulsarla. Ahí la pantalla es para atender, y un
 * formulario siempre abierto le come alto al chat todo el día para algo que se
 * usa cuando pasa algo raro. En Incidencias va abierto: esa pantalla existe
 * justamente para preguntar.
 */
export default function PreguntarAlSistema({ colapsable = false }: { colapsable?: boolean }) {
  const navigate = useNavigate()
  const [abierto, setAbierto] = useState(!colapsable)
  const [pregunta, setPregunta] = useState('')
  const [r, setR] = useState<Diagnostico | null>(null)
  const [cargando, setCargando] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const preguntar = useCallback(async (texto: string) => {
    const q = texto.trim()
    if (!q || cargando) return
    setCargando(true)
    setError(null)
    try {
      const res = await apiClient.post<Diagnostico>('/observability/ask', { pregunta: q })
      setR(res.data)
    } catch (err) {
      setR(null)
      setError(errorMessage(err, 'No se pudo consultar el estado del sistema.'))
    } finally {
      setCargando(false)
    }
  }, [cargando])

  if (!abierto) {
    return (
      <button
        onClick={() => setAbierto(true)}
        className="flex w-full items-center gap-2 border-b border-surface-border bg-purple-light/30 px-4 py-2 text-left text-body text-ink-muted transition-colors hover:bg-purple-light/60 hover:text-purple"
      >
        <span className="shrink-0 text-purple" aria-hidden>
          <svg width="15" height="15" viewBox="0 0 17 17" fill="none">
            <path d="M8.5 1 10.2 6 15 7.7l-4.8 1.7L8.5 15l-1.7-5.6L2 7.7l4.8-1.7z"
                  fill="currentColor" />
          </svg>
        </span>
        ¿Pasó algo? Pregúntale al sistema
      </button>
    )
  }

  return (
    <div className="rounded-card border border-purple-border bg-purple-light/40 p-4">
      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(e) => { e.preventDefault(); void preguntar(pregunta) }}
      >
        <span className="shrink-0 text-purple" aria-hidden>
          <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
            <path d="M8.5 1 10.2 6 15 7.7l-4.8 1.7L8.5 15l-1.7-5.6L2 7.7l4.8-1.7z"
                  fill="currentColor" />
          </svg>
        </span>
        <input
          value={pregunta}
          onChange={(e) => setPregunta(e.target.value)}
          placeholder="¿Qué quieres saber? Por ejemplo: ¿pasó algo con los documentos hoy?"
          className="min-w-[280px] flex-1 rounded-input border border-surface-border bg-white px-3.5 py-2.5 text-body text-ink placeholder:text-ink-faint focus:border-purple focus:outline-none"
        />
        <button
          type="submit"
          disabled={!pregunta.trim() || cargando}
          className="shrink-0 rounded-input bg-purple px-5 py-2.5 text-body font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {cargando ? 'Revisando…' : 'Preguntar'}
        </button>
        {colapsable && (
          <button
            type="button"
            onClick={() => { setAbierto(false); setR(null); setError(null) }}
            className="shrink-0 px-1.5 text-body text-ink-faint transition-colors hover:text-ink"
            aria-label="Cerrar"
          >
            ✕
          </button>
        )}
      </form>

      {!r && !cargando && !error && (
        <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
          {EJEMPLOS.map((e) => (
            <button
              key={e}
              onClick={() => { setPregunta(e); void preguntar(e) }}
              className="rounded-chip border border-surface-border bg-white px-2.5 py-1 text-pill text-ink-muted transition-colors hover:border-purple hover:text-purple"
            >
              {e}
            </button>
          ))}
        </div>
      )}

      {error && (
        <p className="mt-3 rounded-btn border border-danger-border bg-danger-light px-3 py-2 text-body text-danger">
          {error}
        </p>
      )}

      {r && (
        <div className="mt-3 rounded-card border border-surface-border bg-white p-4">
          <div className="flex items-start gap-2.5">
            <span
              className={`mt-1 h-[9px] w-[9px] shrink-0 rounded-full ${
                r.hay_problema ? 'bg-danger' : 'bg-brand'
              }`}
              aria-hidden
            />
            <div className="min-w-0 flex-1">
              <p className="whitespace-pre-wrap text-body leading-relaxed text-ink">
                {r.respuesta}
              </p>

              {r.siguiente_paso && (
                <p className="mt-2 text-body text-ink-muted">
                  <b className="text-ink">Siguiente paso:</b> {r.siguiente_paso}
                </p>
              )}

              {r.referencias?.length > 0 && (
                <div className="mt-3 flex flex-wrap items-center gap-1.5">
                  <span className="text-pill text-ink-faint">Ver el detalle:</span>
                  {r.referencias.map((ref) => (
                    <button
                      key={ref.to}
                      onClick={() => navigate(ref.to)}
                      className="rounded-chip border border-surface-border bg-surface px-2.5 py-1 text-pill font-semibold text-ink-muted transition-colors hover:border-brand hover:bg-white hover:text-brand"
                    >
                      {ref.etiqueta} →
                    </button>
                  ))}
                </div>
              )}

              {/* Siempre visibles: es lo que permite comprobar la respuesta en
                  vez de creerle al modelo, y deja claro cuando algo NO se miró
                  (los logs necesitan rol Admin). */}
              <p className="mt-3 border-t border-surface-faint pt-2 text-meta text-ink-faint">
                {r.disponible
                  ? `Consultado: ${r.fuentes.join(' · ')}`
                  : `${r.motivo} · Consultado: ${r.fuentes.join(' · ')}`}
                {r.alcance && <><br />Alcance de tu rol: {r.alcance}</>}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
